import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, FOUNDATION_ROUTES, getLabelTipoEstipulante, Route, shouldRenderMobile } from '@philips-emr/hpms-stipulator-commons';
import MODULES from '@philips-emr/hpms-stipulator-commons/lib/constants/modules';
import TokenConstants from '@philips-emr/hpms-stipulator-commons/lib/constants/token-constants';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { TermsService } from '@philips-emr/hpms-stipulator-commons/lib/services/terms-service';
import { HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { AvatarAPI, DropDownMenuAPI, LinkAPI, PanelAPI, RepeaterAPI, TextAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnClick, OnMount, OnOpen } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { AuthenticationService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';
import { applicationContext } from '@philips-emr/tws-foundation';
import { foundationContext } from '@philips-emr/tws-foundation/lib/context';
import { isEmptyOrBlankString } from '@philips-emr/tws-utils';

import './stipulator.scss';
import Audit from '../../app/audit';
import { FragmentNames } from '../../codegen';
import { StipulatorAliases as Aliases } from '../../codegen/constants/aliases/stipulator-aliases';
import BaseController from '../BaseController';
import { Mensagem } from '../mensagem';

interface TermoUso {
  nrSeqTermo: number;
  nrSeqTermoUso: number;
}

export const EVENT_UPDATE_COUNT_MSG_FLAG = 'event_update_count_msg_flag';
export const EVENT_CLOSE_DROPDOWN = 'event_close_dropdown';

@Controller({ fragment: FragmentNames.STIPULATOR })
export default class StipulatorController extends BaseController {

  @Inject(Aliases.TEXT_NR_CONTRATO)
  private textNrContratoAPI: TextAPI;
  @Inject(Aliases.TEXT_DS_ESTIPULANTE)
  private textDsEstipulanteAPI: TextAPI;
  @Inject(Aliases.TEXT_LABEL_ESTIPULANTE)
  private textLabelEstipulanteAPI: TextAPI;
  @Inject(Aliases.TEXT_CD_ESTIPULANTE)
  private textCdEstipulanteAPI: TextAPI;
  @Inject(Aliases.TEXT_NM_SUBESTIPULANTE)
  private textNmSubestipulante: TextAPI;
  @Inject(Aliases.PANEL_SUBESTIPULANTE)
  private panelSubestipulante: PanelAPI;
  @Inject(Aliases.REPEATER_CONTRATOS)
  private repeaterContratosAPI: RepeaterAPI;
  @Inject(Aliases.DROP_DOWN_PROFILES)
  private dropDownProfilesAPI: DropDownMenuAPI;
  @Inject(Aliases.AVATAR_USER)
  private avatarUserAPI: AvatarAPI;
  @Inject(Aliases.PANEL_DROPDOWN_PROFILE)
  private panelDropDownApi: PanelAPI;
  @Inject(Aliases.PANEL_TEXT_PROFILE)
  private panelTextApi: PanelAPI;
  @Inject(Aliases.TEXT_PROFILE)
  private textProfileApi: TextAPI;
  @Inject(Aliases.LINK_MENSAGEM)
  private linkMensagem: LinkAPI;

  @OnMount(Aliases.LAYOUT_STIPULATOR)
  onMount(): void {
    if (applicationContext.getCurrentModule()?.id !== 'inicio') {
      applicationContext.openModule('inicio');
    }
    TermsService.checkTerms();
    foundationContext.setShowEstablishment(false);
    foundationContext.setShowSector(false);
  }

  @OnMount(Aliases.PANEL_PROFILES)
  onPanelProfilesMount(): void {
    let { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    nrSeqContrato = parseInt(nrSeqContrato as string);

    this.panelTextApi.setVisible(false);

    const user = foundationContext.getUser();
    if (user) {
      let username = user.name;

      if (username.length > 30) {
        username = username.substring(0, 30);
        username = `${username}${'...'}`;
      }
      catchRenderException(() => {
        if (!shouldRenderMobile()) {
          this.avatarUserAPI.setTitle(username);
        }
      });
    }

    const currentContract = StipulatorContext.getCurrentContract();

    if (currentContract.nrSeqContrato !== nrSeqContrato) {
      const contract = StipulatorContext.getContracts().find(contract => contract.nrSeqContrato === nrSeqContrato);

      if (contract) {
        StipulatorContext.setCurrentContract(contract.nrSeqContrato);
      } else {
        StipulatorContext.setCurrentContract(currentContract.nrSeqContrato);
        this.validateRoutes();
      }

    }

    if (StipulatorContext.getContracts().length < 2) {
      this.panelDropDownApi.setVisible(false);
      this.panelTextApi.setVisible(true);
    }

    this.updateDataContext();
    this.setValues();
  }

  @OnMount(Aliases.PANEL_CONTRATOS)
  onPanelContratosMount(): void {
    const data: Record<string, Value>[] = [];
    const currentContract = StipulatorContext.getCurrentContract();
    const contracts = StipulatorContext.getContracts();

    contracts.forEach(contract => {

      if (contract.nrSeqContrato !== currentContract.nrSeqContrato) {
        const label = getLabelTipoEstipulante(contract.ieTipoEstipulante);

        data.push({
          title: `${contract.nrContrato} - ${contract.dsEstipulante}`,
          subtitle: `${label}: ${contract.cdEstipulante}`,
          nrSeqContrato: contract.nrSeqContrato,
        });
      }
    });

    this.repeaterContratosAPI.setData(data);
  }

  @OnClick(Aliases.OPTION_EXIT)
  onExitClick(): void {
    void AuthenticationService.logout();
    applicationContext.redirect(FOUNDATION_ROUTES.LOGIN);
  }

  @OnClick(Aliases.OPTION_MEUS_DADOS)
  onClickMeusDados(): void {
    this.historyService.push(Route.MEUS_DADOS);
  }

  @OnClick(Aliases.OPTION_MENSAGEM)
  onClickMensagem = (): void => this.historyService.push(Route.MENSAGENS);

  @OnClick(Aliases.OPTION_DADOS_GRUPO_CONTRATUAL)
  onDadosGrupoContratualClick(): void {
    ModalService.openDefault(this.modalDadosGrupoContratual());
  }

  @HPMSOnOptionClick(Aliases.HPMS_CONTRATOS_OPTIONS)
  onOptionClick(event: ClickEvent): void {
    StipulatorContext.setCurrentContract(event.value);

    this.setValues();
    this.validateRoutes();

    this.dropDownProfilesAPI.close();
  }

  private validateRoutes(): void {

    const token = AuthenticationService.getJWTToken();
    const modules = token && token.claims[TokenConstants.MODULE] as string;
    const match = this.historyService.getMatch();

    const params = {
      ...match.params,
      nrSeqContrato: StipulatorContext.getCurrentContract().nrSeqContrato,
    };

    if (modules) {
      const route = MODULES[modules];

      this.historyService.push(route ? route : APPLICATION_ROUTES.DASHBOARD, params);
    } else {
      this.historyService.push(APPLICATION_ROUTES.DASHBOARD, params);
    }

  }

  private setValues(): void {
    const contract = StipulatorContext.getCurrentContract();

    catchRenderException(() => this.textNrContratoAPI.setValue(`${contract.nrContrato || ''}`));
    catchRenderException(() => this.textDsEstipulanteAPI.setValue(`${contract.dsEstipulante || ''}`));
    catchRenderException(() => this.textProfileApi.setValue(`${contract.dsEstipulante || ''}`));
    catchRenderException(() => this.textCdEstipulanteAPI.setValue(`${contract.cdEstipulante || ''}`));
    catchRenderException(() => {
      const nmSubestipulante = `${contract.nmSubestipulante || ''}`;

      this.panelSubestipulante.setVisible(!isEmptyOrBlankString(nmSubestipulante));
      this.textNmSubestipulante.setValue(nmSubestipulante);
    });
    catchRenderException(() => this.textLabelEstipulanteAPI.setValue(getLabelTipoEstipulante(`${contract.ieTipoEstipulante || ''}`)));
  }

  @OnMount(Aliases.PANEL_CONTEXT_MENU_CONTRATOS)
  onMountDadosContratual(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const contract = StipulatorContext.getCurrentContract();
    catchRenderException(() => setTimeout(() => api.setVisible(contract.ieOrigemGrupoWeb == 3 || contract.ieOrigemGrupoWeb == 4)));
  }

  private modalDadosGrupoContratual(): ModalProps {
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: FragmentNames.DADOS_GRUPO_CONTRATUAL,
      title: '$expression.$1032296',
      contextData: {},
    };
    return modalWithFragment;
  }

  private updateDataContext(): void {
    const { nrSeqContrato, nrSeqSubestipulante } = StipulatorContext.getCurrentContract();

    this.context.set({ nrSeqContrato, nrSeqSubestipulante });
  }

  @OnMount(Aliases.LINK_MENSAGEM)
  onMountNotificacaoMensagem(): void {
    this.fetchMensagensNaoLidas();

    this.context.set({ [EVENT_UPDATE_COUNT_MSG_FLAG]: (): void => this.fetchMensagensNaoLidas() });
  }

  @OnMount(Aliases.DROP_DOWN_MENSAGEM)
  onMountDropDownMensagem(event: MountEvent): void {
    const api = event.target as unknown as DropDownMenuAPI;
    this.context.set({ [EVENT_CLOSE_DROPDOWN]: (): void => api.close() });
  }

  public fetchMensagensNaoLidas(): void {
    void HttpService.get(Endpoint.GET.MENSAGENS_NAO_LIDAS, {}).then(response => {
      const { ieNaoLida, qtNaoLida } = response.body as Mensagem;

      if (ieNaoLida == 'S') {
        catchRenderException(() => this.linkMensagem.setBadge(qtNaoLida));
      }
    });
  }

  private async checkAvisoPrivacidade(): Promise<void> {

    await HttpService.get(Endpoint.GET.CHECK_AVISO_DE_PRIVACIDADE).then(async respCheckAviso => {
      const { nrSeqTermoUso } = respCheckAviso.body as TermoUso;
      if (!nrSeqTermoUso) {
        await HttpService.get(Endpoint.GET.AVISO_DE_PRIVACIDADE_ATUAL).then(respAviso => {
          const { nrSeqTermoUso } = respAviso.body as TermoUso;
          if (nrSeqTermoUso) {
            this.context.set({ nrSeqAviso: nrSeqTermoUso });
            this.historyService.push(Route.ASSIGN_AVISO_DE_PRIVACIDADE, { nrSeqAviso: nrSeqTermoUso });
          }
        });
      }
    });
  }

  private async checkTermosdeUso(): Promise<void> {
    await HttpService.get(Endpoint.GET.CHECK_TERMOS_USO).then(async (result) => {
      const { nrSeqTermoUso } = result.body as TermoUso;
      if (!nrSeqTermoUso) {
        await HttpService.get(Endpoint.GET.TERMO_USO_ATUAL).then(resp => {
          const { nrSeqTermoUso } = resp.body as TermoUso;
          if (nrSeqTermoUso) {
            this.context.set({ nrSeqTermo: nrSeqTermoUso });
            this.historyService.push(Route.ASSIGN_TERMO_DE_USO, { nrSeqTermo: nrSeqTermoUso });
          }
        });
      }
    });
  }

  async checkTermoDeUso(): Promise<void> {
    await this.checkAvisoPrivacidade();
    await this.checkTermosdeUso();
  }

  @OnOpen(Aliases.DROP_DOWN_MENSAGEM)
  onOpenDropDownMensagem(): void {
    const requestBody = {
      rota: '',
      nmFuncao: MessageService.translate('stipulator.mensagens'),
      'refresh_token': '',
    };
    void Audit.logAcessoFuncao(requestBody);
  }

  @OnClick(Aliases.LINK_MOSTRAR_TODAS_MENSAGENS)
  onClickLinkMostrarTodasMensagens = () : void => this.historyService.push(Route.MENSAGENS);
}
